/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { format, subDays } from 'date-fns';
import { Flame, Trophy } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { useGetTournamentAnalyticsQuery } from '../../domain/api/analytics-api';
import AnalyticsFilters from '../components/analytics-filters';
import KpiGrid from '../components/kpi-grid';
import MatchRecordCard from '../components/match-record-card';
import FastestGoalCard from '../components/fastest-goal-card';

const PageBlock = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RecordsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.WhiteGray};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

interface FilterState {
  championshipId?: number | null;
  startDate: string;
  endDate: string;
}

function AnalyticsPage() {
  const { setHeaderContent } = useMainLayout();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const [filters, setFilters] = useState<FilterState>({
    championshipId: null,
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Аналитика</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const handleFiltersChange = useCallback((incoming: FilterState) => {
    setFilters(incoming);
  }, []);

  const { data, isLoading } = useGetTournamentAnalyticsQuery(
    {
      tournamentId: tournamentId!,
      championshipId: filters.championshipId,
      startDate: filters.startDate,
      endDate: filters.endDate,
    },
    { skip: !tournamentId },
  );

  return (
    <PageBlock>
      {tournamentId && (
        <AnalyticsFilters tournamentId={tournamentId} onFiltersChange={handleFiltersChange} />
      )}

      <KpiGrid summary={data?.summary} loading={isLoading} />

      <RecordsSection>
        <SectionTitle>Рекорды</SectionTitle>
        <MatchRecordCard
          title="Самый результативный матч"
          icon={<Flame size={20} />}
          record={data?.records.mostProductiveMatch ?? null}
          highlightLabel="Голов"
        />
        <MatchRecordCard
          title="Самая крупная победа"
          icon={<Trophy size={20} />}
          record={data?.records.biggestWin ?? null}
          highlightLabel="Разница"
        />
        <FastestGoalCard record={data?.records.fastestGoal ?? null} />
      </RecordsSection>
    </PageBlock>
  );
}

export default AnalyticsPage;
